import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { IBlocksType } from './ContextType';
import { useDefaultContext } from './HomeContext';

const getSocketURL = (networkId: string) => {
  const NETWORK_URLS: Record<string, string> = {
    "newtestnet": process.env.REACT_APP_SSE_URL_DEVNET || "",
    "testnet": process.env.REACT_APP_SSE_URL_TESTNET || "",
  };

  return NETWORK_URLS[networkId] || process.env.REACT_APP_DEFAULT_SOCKET_URL;
};

type TCount = {
  currentCounts: string;
  chaincodeCount: string;
  latestBlock: string;
  peerCount: number | string;
  txCount: string;
};

interface SocketContextProps {
  count?: TCount;
  networkId: string;
  blocks: IBlocksType[],
  transactions: any[],
  tpsCount:any
};

const SocketContext = createContext<SocketContextProps | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [count, setCount] = useState<TCount | undefined>(undefined);
  const [blocks, setBlocks] = useState<IBlocksType[]>([]);
  const [transactions, setTransactions] = useState<any>([]);
  const { chainNetwork } = useDefaultContext();
  const [tpsCount, setTpsCount] = useState(0);
  const [networkId, setNetworkId] = useState<string>(localStorage.getItem("network")?.toLowerCase() || "");
  // Function to initialize socket connection
  const connectSocket = (networkId: string) => {
    const socketURL = getSocketURL(networkId);

    const socket = io(socketURL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => {
      socket.emit("join-event-room", "BLOCKS", networkId);
      socket.emit("join-event-room", "TRANSACTIONS", networkId);
      socket.emit("join-event-room", "CHAINCODE", networkId);
      socket.emit("join-last-60sec-transaction-room", networkId);
      socket.emit("join-latest-room", {
        roomType: "LATEST_TRANSACTIONS",
        network: networkId
      });
      socket.emit("join-latest-room", {
        roomType: "LATEST_BLOCKS",
        network: networkId
      });
      socket.emit("join-latest-room", {
        roomType: "LATEST_CHAINCODE",
        network: networkId
      });
    });

    socket.on("latest-transactions-event", (notification) => {
      setTransactions(notification.items)
    });

    socket.on("last-60sec-transaction-event", (data) => {
      setTpsCount(data.transactionCount)
    })

    socket.on("latest-blocks-event", (notification) => {
      setBlocks(notification.items)
    });

    socket.on("transaction-event", (notification) => {
      setCount(notification.currentCounts);
    });

    socket.on("disconnect", () => {
      console.warn("Socket disconnected.");
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    socketRef.current = socket;
  };

  // Function to handle network change from localStorage
  const handleNetworkChange = () => {
    const storedNetworkId = localStorage.getItem("network")?.toLowerCase() || "";

    if (storedNetworkId !== networkId) {

      // Disconnect old socket
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      // Update state & reconnect with the new network URL
      setNetworkId(storedNetworkId);
      connectSocket(storedNetworkId);
    }
  };

  useEffect(() => {
    // Initial socket connection
    connectSocket(chainNetwork.toLowerCase() || localStorage.getItem("network")?.toLowerCase() || '');

    // Listen for network changes in localStorage
    handleNetworkChange();

    return () => {
      // Cleanup when component unmounts
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [chainNetwork, localStorage.getItem("network")]);

  return <SocketContext.Provider value={{ count, networkId, transactions, blocks, tpsCount }}>{children}</SocketContext.Provider>;
};
